import React from "react";
import { motion } from "framer-motion";
import { Popover } from "antd";
import { Calendar, ChevronDown } from "lucide-react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { useDashTheme } from "../hooks/useDashTheme";
import AntRangePicker from "../../../components/UI/AntRangePicker";
import AntDatePicker from "../../../components/UI/AntDatePicker";

// ── Range helpers ───────────────────────────────────────────────────────────
// Canonical { start, end, key } shape used across all V2 pages. Times are
// snapped to the day boundaries so the backend gets a full inclusive day.
export function rangeForPreset(key) {
  const start = new Date();
  const end = new Date();
  if (key === "today" || key === "1d") { /* same day */ }
  else if (key === "yesterday") { start.setDate(start.getDate() - 1); end.setDate(end.getDate() - 1); }
  else if (/^(\d+)d$/.test(key)) start.setDate(end.getDate() - (Math.max(1, parseInt(key, 10)) - 1));
  else if (key === "week") { const dow = (start.getDay() + 6) % 7; start.setDate(start.getDate() - dow); } // Mon–today
  else if (key === "month") start.setDate(1);
  else if (key === "lastMonth") {
    start.setMonth(start.getMonth() - 1, 1);
    end.setDate(1); end.setDate(0); // last day of previous month
  }
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end, key };
}

// Backwards-compatible alias (some screens import presetToRange).
export const presetToRange = rangeForPreset;

const DEFAULT_PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "month", label: "This month" },
];

/**
 * DateRange — the single V2 date-range control.
 *
 * Quick-preset chips (V2 styled) + an AntD RangePicker calendar for arbitrary
 * custom ranges. Picking from the calendar flips the active state to "custom".
 *
 * Props:
 *  - range      : { start, end, key }              (current value)
 *  - onChange   : (next:{start,end,key}) => void
 *  - presets    : [{ key, label }]                 (optional; defaults above)
 *  - maxDate    : Date | string                    (optional; blocks later days — e.g. AI pages cap at "yesterday")
 *  - layoutId   : string                           (unique framer layoutId for the active pill)
 *  - pickerWidth: number                           (calendar input width, default 280)
 */
export function DateRange({ range, onChange, presets = DEFAULT_PRESETS, maxDate, maxSpanDays, layoutId = "v2RangeChip", pickerWidth = 280 }) {
  const t = useDashTheme();
  const maxDay = maxDate ? dayjs(maxDate).endOf("day") : null;

  // Only the future cap affects which days are clickable — selection stays fully
  // free (1 day, 2 days, anything). The max-span limit is enforced at confirm
  // time (onChange) so the calendar never fights the user or jumps months.
  const disabledDate = maxDay ? (cur) => cur && cur.isAfter(maxDay, "day") : undefined;

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: t.s[2], flexWrap: "wrap" }}>
      {/* Chips wrap onto another line rather than pushing the calendar out of
          the container — a preset that runs past the edge reads as overlap. */}
      <div style={{ display: "inline-flex", flexWrap: "wrap", rowGap: 2, padding: 2, gap: 2, background: t.c.surfaceMuted, borderRadius: 16, border: `1px solid ${t.c.border}`, maxWidth: "100%" }}>
        {presets.map((p) => {
          const active = range?.key === p.key;
          return (
            <button
              key={p.key}
              onClick={() => onChange(rangeForPreset(p.key))}
              style={{
                position: "relative", height: 24, padding: "0 12px", borderRadius: 14, border: "none",
                background: "transparent", color: active ? t.c.text : t.c.textSecondary,
                fontSize: t.font.size.md, fontWeight: active ? 500 : 400, cursor: "pointer",
                fontFamily: t.font.family, zIndex: 1, transition: "color 140ms", whiteSpace: "nowrap",
              }}
            >
              {active && (
                <motion.span layoutId={layoutId} transition={t.motion.spring.snappy}
                  style={{ position: "absolute", inset: 0, background: t.c.surface, borderRadius: 14, boxShadow: t.shadow.sm, zIndex: -1 }} />
              )}
              {p.label}
            </button>
          );
        })}
      </div>
      <div style={{ flex: `1 1 ${pickerWidth}px`, minWidth: 0, maxWidth: "100%" }}>
        <AntRangePicker
          className="dashv2-rangepicker"
          value={range?.start && range?.end ? [range.start, range.end] : null}
          showPresets={false}
          allowClear={false}
          disabledDate={disabledDate}
          onChange={(dates) => {
            if (!dates || !dates[0] || !dates[1]) { onChange(rangeForPreset(presets[0]?.key || "today")); return; }
            let start = new Date(dates[0]); start.setHours(0, 0, 0, 0);
            let end = new Date(dates[1]); end.setHours(23, 59, 59, 999);

            // Enforce the max-span limit only at confirm time. If the picked
            // range is wider than allowed, clamp the END so the start is kept,
            // and let the user know.
            if (maxSpanDays) {
              const spanDays = Math.floor((end - start) / 86400000) + 1;
              if (spanDays > maxSpanDays) {
                const clamped = new Date(start);
                clamped.setDate(clamped.getDate() + (maxSpanDays - 1));
                clamped.setHours(23, 59, 59, 999);
                end = clamped;
                const months = Math.round(maxSpanDays / 30);
                toast.error(`Date range is limited to ${maxSpanDays} days (~${months} month${months > 1 ? "s" : ""}). Trimmed to fit.`);
              }
            }
            onChange({ start, end, key: "custom" });
          }}
        />
      </div>
    </div>
  );
}

/**
 * DateRangeTool — compact `.tg-filterbar` trigger for DateRange.
 *
 * The full control (preset chips + calendar) needs ~500-600px inline, which
 * wraps and breaks the fixed-height toolbar row next to page tabs. This
 * collapses it into a single 28px `tg-tool` button (icon + current range
 * label, matching ToolSelect) that opens the real DateRange in a popover —
 * same props as DateRange, just rendered on demand.
 */
export function DateRangeTool({ placement = "bottomRight", panelWidth = 336, ...props }) {
  const t = useDashTheme();
  const [open, setOpen] = React.useState(false);
  const { range, onChange, presets = DEFAULT_PRESETS } = props;

  const activePreset = presets.find((p) => p.key === range?.key);
  const label = activePreset?.label
    || (range?.start && range?.end
      ? `${new Date(range.start).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(range.end).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
      : "Select range");

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement={placement}
      arrow={false}
      overlayInnerStyle={{
        padding: 10, background: t.c.surface,
        border: `1px solid ${t.c.border}`, borderRadius: t.r.md, boxShadow: t.shadow.lg,
      }}
      content={
        <div style={{ width: panelWidth }}>
          <DateRange {...props} onChange={(r) => { onChange(r); setOpen(false); }} />
        </div>
      }
    >
      <button type="button" className={`tg-tool ${open ? "is-open" : ""}`}>
        <Calendar size={13} />
        <span className="tg-tool-value">{label}</span>
        <ChevronDown size={13} />
      </button>
    </Popover>
  );
}

/**
 * DateTool — compact `.tg-filterbar` trigger for a single date (not a range).
 * Same idea as DateRangeTool: a 28px `tg-tool` button showing the current
 * date, opening the real AntDatePicker in a popover (autofocused, so its
 * calendar appears immediately — no double click).
 */
export function DateTool({ value, onChange, placeholder = "Select date", disabledDate, placement = "bottomRight", icon }) {
  const t = useDashTheme();
  const [open, setOpen] = React.useState(false);
  const label = value
    ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : placeholder;

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement={placement}
      arrow={false}
      overlayInnerStyle={{
        padding: 10, background: t.c.surface,
        border: `1px solid ${t.c.border}`, borderRadius: t.r.md, boxShadow: t.shadow.lg,
      }}
      content={
        <div style={{ width: 280 }}>
          <AntDatePicker
            autoFocus
            value={value || null}
            onChange={(_, ds) => { onChange(ds || ""); setOpen(false); }}
            disabledDate={disabledDate}
            getPopupContainer={(node) => node.parentNode}
          />
        </div>
      }
    >
      <button type="button" className={`tg-tool ${open ? "is-open" : ""}`}>
        {icon || <Calendar size={13} />}
        <span className="tg-tool-value">{label}</span>
        <ChevronDown size={13} />
      </button>
    </Popover>
  );
}

export default DateRange;
